/**
 * The fallback stage. Owned by `look`.
 *
 * WHAT THIS IS: a minimal street — road ribbon, kerbs, sidewalks, lane dashes, a facade wall
 * either side, a lamp post, a few cones — that exists ONLY while `ctx.world.isFallback` is
 * true. It is a LIGHTING TEST RIG, not content. It lets the lighting, fog, shadows, sky,
 * material library and post grade be built and graded before the city lands, which is the
 * only way eight agents can work in parallel.
 *
 * IT DELETES ITSELF. `LookSystem.update()` checks `ctx.world.isFallback` every frame and
 * disposes this the instant `world` publishes a real WorldQuery. If you can see this
 * geometry in a finished build, that is a bug in `look`, not a design decision.
 *
 * Everything here uses the shared MaterialLibrary keys, so it doubles as a live smoke test
 * that every key returns something sensible.
 */

import * as THREE from 'three';
import { LOOK_TUNING as L } from './tuning.js';

export class FallbackStage {
  /**
   * @param {object} ctx
   * @param {import('../core/rng.js').Rng} rng
   */
  constructor(ctx, rng) {
    this.ctx = ctx;
    this.group = new THREE.Group();
    this.group.name = 'look.fallbackStage';
    this._geos = [];
    this._built = false;
    this.rng = rng;
  }

  build() {
    if (this._built) return this.group;
    this._built = true;

    const F = L.fallbackGround;
    const mats = this.ctx.materials;
    const rng = this.rng;
    const g = this.group;

    /**
     * Materials are SHARED, so `texture.repeat` cannot vary per mesh. Tile by baking the
     * scale into the geometry's UVs instead — this is the pattern `world` should use too.
     * `metres` is how many world metres one texture tile covers.
     */
    const tileUV = (geo, su, sv) => {
      const uv = geo.attributes.uv;
      for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * su, uv.getY(i) * sv);
      uv.needsUpdate = true;
      return geo;
    };
    const plane = (w, h, metres = 0) => {
      const q = new THREE.PlaneGeometry(w, h);
      this._geos.push(q);
      if (metres > 0) tileUV(q, w / metres, h / metres);
      return q;
    };
    const box = (w, h, d, metres = 0) => {
      const q = new THREE.BoxGeometry(w, h, d);
      this._geos.push(q);
      if (metres > 0) tileUV(q, Math.max(w, d) / metres, h / metres);
      return q;
    };

    // ── ground plate: the boulevard either side of the road ──────────────────────────
    {
      const geo = plane(F.planeSize, F.planeSize, 3.2);
      const m = new THREE.Mesh(geo, mats.get('road.sidewalk'));
      m.rotation.x = -Math.PI / 2;
      m.position.y = F.sidewalkHeight;
      m.receiveShadow = true;
      m.name = 'fallback.ground';
      g.add(m);
    }

    // ── the road ribbon ─────────────────────────────────────────────────────────────
    {
      const geo = plane(F.roadWidth, F.roadLength, 7.5);
      const m = new THREE.Mesh(geo, mats.get('road.asphalt'));
      m.rotation.x = -Math.PI / 2;
      m.position.y = 0.002;
      m.receiveShadow = true;
      m.name = 'fallback.road';
      g.add(m);
    }

    // ── kerbs ───────────────────────────────────────────────────────────────────────
    for (const side of [-1, 1]) {
      const geo = box(0.34, F.kerbHeight, F.roadLength);
      const m = new THREE.Mesh(geo, mats.get('road.curb'));
      m.position.set(side * (F.roadWidth * 0.5 + 0.17), F.kerbHeight * 0.5, 0);
      m.castShadow = true; m.receiveShadow = true;
      m.name = 'fallback.kerb';
      g.add(m);
    }

    // ── lane dashes (one instanced mesh, one draw call) ─────────────────────────────
    {
      const geo = plane(0.16, F.laneDashLength);
      const count = F.laneCount;
      const inst = new THREE.InstancedMesh(geo, mats.get('road.markings'), count);
      inst.name = 'fallback.lanedash';
      const mtx = new THREE.Matrix4();
      const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
      const s = new THREE.Vector3(1, 1, 1);
      const p = new THREE.Vector3();
      const pitch = F.laneDashLength + F.laneDashGap;
      for (let i = 0; i < count; i++) {
        p.set(0, 0.012, (i - count / 2) * pitch);
        mtx.compose(p, q, s);
        inst.setMatrixAt(i, mtx);
      }
      inst.instanceMatrix.needsUpdate = true;
      inst.frustumCulled = false;
      g.add(inst);
    }
    // Solid edge lines.
    for (const side of [-1, 1]) {
      const geo = plane(0.14, F.roadLength);
      const m = new THREE.Mesh(geo, mats.get('road.markings'));
      m.rotation.x = -Math.PI / 2;
      m.position.set(side * (F.roadWidth * 0.5 - 0.42), 0.012, 0);
      g.add(m);
    }

    // ── facade walls, so shadows and value separation are gradeable ─────────────────
    // Buildings must read DARKER than the road. Three material families so the library's
    // brick / stone / render keys all get exercised.
    const facadeKeys = ['building.brick', 'building.stone', 'building.render', 'building.brick.ochre', 'building.brick.painted'];
    for (const side of [-1, 1]) {
      let z = -180;
      while (z < 180) {
        const w = rng.range(9, 15);
        const h = rng.range(9.5, 15.5);
        const d = rng.range(10, 16);
        const key = rng.pick(facadeKeys);
        const bodyGeo = box(w, h, d, 3.6);
        const body = new THREE.Mesh(bodyGeo, mats.get(key));
        body.position.set(side * (F.roadWidth * 0.5 + F.sidewalkWidth + d * 0.5), h * 0.5 + F.sidewalkHeight, z + w * 0.5);
        body.rotation.y = Math.PI / 2;
        body.castShadow = true;
        body.receiveShadow = true;
        body.name = 'fallback.facade';
        g.add(body);

        // A window band on the street-facing side.
        const winGeo = plane(w * 0.86, h * 0.5, 4.2);
        const win = new THREE.Mesh(winGeo, mats.get('building.window'));
        win.position.set(
          side * (F.roadWidth * 0.5 + F.sidewalkWidth - 0.02),
          F.sidewalkHeight + h * 0.55,
          z + w * 0.5
        );
        win.rotation.y = side < 0 ? Math.PI / 2 : -Math.PI / 2;
        win.name = 'fallback.window';
        g.add(win);

        // Cornice — the flat-roof-with-a-lip silhouette that says Montreal.
        const corGeo = box(w + 0.5, 0.55, d + 0.5);
        const cor = new THREE.Mesh(corGeo, mats.get('building.trim'));
        cor.position.set(body.position.x, F.sidewalkHeight + h + 0.2, body.position.z);
        cor.rotation.y = Math.PI / 2;
        cor.castShadow = true;
        g.add(cor);

        z += w + rng.range(0.4, 2.2);
      }
    }

    // ── a lamp post with a real emissive lens, so the night grade has something to bloom ──
    {
      const postGeo = box(0.16, 7.2, 0.16);
      const lensGeo = box(0.55, 0.20, 0.9);
      for (let i = -3; i <= 3; i++) {
        for (const side of [-1, 1]) {
          const x = side * (F.roadWidth * 0.5 + 0.9);
          const zz = i * 34 + (side < 0 ? 17 : 0);
          const post = new THREE.Mesh(postGeo, mats.get('prop.metal'));
          post.position.set(x, 3.6 + F.sidewalkHeight, zz);
          post.castShadow = true;
          g.add(post);
          const lens = new THREE.Mesh(lensGeo, mats.get('sign.emissive'));
          lens.position.set(x - side * 0.55, 7.1 + F.sidewalkHeight, zz);
          g.add(lens);
        }
      }
    }

    // ── cones, because this is Montreal ─────────────────────────────────────────────
    {
      const coneGeo = new THREE.ConeGeometry(0.24, 0.66, 12, 1, true);
      this._geos.push(coneGeo);
      const baseGeo = box(0.44, 0.05, 0.44);
      const count = 26;
      const inst = new THREE.InstancedMesh(coneGeo, this.ctx.materials.get('prop.cone'), count);
      const instBase = new THREE.InstancedMesh(baseGeo, this.ctx.materials.get('truck.tyre'), count);
      inst.name = 'fallback.cones';
      inst.castShadow = true;
      instBase.castShadow = true;
      const mtx = new THREE.Matrix4();
      const q = new THREE.Quaternion();
      const s = new THREE.Vector3(1, 1, 1);
      const p = new THREE.Vector3();
      for (let i = 0; i < count; i++) {
        const side = i % 2 === 0 ? -1 : 1;
        const zz = -110 + i * 9 + rng.range(-2, 2);
        const xx = side * rng.range(1.2, 4.6);
        p.set(xx, 0.33, zz);
        mtx.compose(p, q, s);
        inst.setMatrixAt(i, mtx);
        p.y = 0.026;
        mtx.compose(p, q, s);
        instBase.setMatrixAt(i, mtx);
      }
      inst.instanceMatrix.needsUpdate = true;
      instBase.instanceMatrix.needsUpdate = true;
      inst.frustumCulled = false;
      instBase.frustumCulled = false;
      g.add(inst, instBase);
    }

    // ── one open pothole and one fresh patch, to prove the value ramp ───────────────
    {
      const holeGeo = new THREE.CircleGeometry(1.15, 20);
      this._geos.push(holeGeo);
      const hole = new THREE.Mesh(holeGeo, mats.get('road.hole'));
      hole.rotation.x = -Math.PI / 2;
      hole.position.set(-2.1, 0.008, -14);
      g.add(hole);

      const patchGeo = new THREE.CircleGeometry(1.05, 20);
      this._geos.push(patchGeo);
      const patch = new THREE.Mesh(patchGeo, mats.get('road.patch.fresh'));
      patch.rotation.x = -Math.PI / 2;
      patch.position.set(2.3, 0.008, -26);
      g.add(patch);

      const curedGeo = new THREE.CircleGeometry(0.95, 20);
      this._geos.push(curedGeo);
      const cured = new THREE.Mesh(curedGeo, mats.get('road.patch.cured'));
      cured.rotation.x = -Math.PI / 2;
      cured.position.set(-2.6, 0.008, -38);
      g.add(cured);
    }

    // ── a stand-in truck so there is a hero object casting a hard shadow ────────────
    {
      const cab = box(2.3, 1.5, 2.4);
      const bed = box(2.3, 1.35, 3.6);
      const mCab = new THREE.Mesh(cab, mats.get('truck.paint'));
      mCab.position.set(0, 1.35, 1.6);
      mCab.castShadow = true;
      const mBed = new THREE.Mesh(bed, mats.get('truck.hotbox'));
      mBed.position.set(0, 1.25, -1.3);
      mBed.castShadow = true;
      const beaconGeo = box(0.5, 0.16, 0.22);
      const beacon = new THREE.Mesh(beaconGeo, mats.get('truck.light.beacon'));
      beacon.position.set(0, 2.2, 1.6);
      const truck = new THREE.Group();
      truck.name = 'fallback.truck';
      truck.add(mCab, mBed, beacon);
      truck.position.set(-2.4, 0, -6);
      g.add(truck);
    }

    return g;
  }

  dispose() {
    for (const geo of this._geos) geo.dispose();
    this._geos.length = 0;
    this.group.traverse((o) => {
      if (o.isInstancedMesh) o.dispose();
      if (o.geometry && !this._geos.includes(o.geometry)) o.geometry.dispose();
    });
    this.group.clear();
    this._built = false;
  }
}

export default FallbackStage;
